import { jsx as _jsx } from "react/jsx-runtime";
// useDesktop.tsx -- desktop-wide selection, drag, clipboard, preview state.
//
// Lives one layer below useAgent: every desktop component (Canvas, DesktopIcon,
// TrashIcon, PreviewPane, ContextMenu) reads from this hook to coordinate
// what's selected, what's being dragged, what's in the clipboard for paste,
// and which file (if any) the preview pane should render.
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
const DesktopContext = createContext(null);
export function DesktopProvider({ children }) {
    const [selected, setSelected] = useState(new Set());
    const [lastSelectedId, setLastSelectedId] = useState(null);
    const [drag, setDrag] = useState(null);
    const [dragOverTarget, setDragOverTarget] = useState(null);
    const [clipboard, setClipboard] = useState(null);
    const [clipboardKind, setClipboardKind] = useState(null);
    const [previewItemId, setPreviewItemId] = useState(null);
    const select = useCallback((entry, multi, range) => {
        setSelected((prev) => {
            // Note: range-select needs visible-order context which lives in the
            // canvas. This implementation handles range as multi-select for now;
            // the canvas can override with full range semantics by calling
            // selectMany directly.
            if (range && lastSelectedId) {
                // Treat as multi for the moment.
                const next = new Set(prev);
                next.add(entry.itemId);
                return next;
            }
            if (multi) {
                const next = new Set(prev);
                if (next.has(entry.itemId))
                    next.delete(entry.itemId);
                else
                    next.add(entry.itemId);
                return next;
            }
            return new Set([entry.itemId]);
        });
        setLastSelectedId(entry.itemId);
    }, [lastSelectedId]);
    const selectMany = useCallback((ids) => {
        setSelected(new Set(ids));
        if (ids.length > 0)
            setLastSelectedId(ids[ids.length - 1]);
    }, []);
    const clear = useCallback(() => {
        setSelected(new Set());
        setLastSelectedId(null);
    }, []);
    const isSelected = useCallback((itemId) => selected.has(itemId), [selected]);
    const startDrag = useCallback((p) => {
        setDrag(p);
    }, []);
    const endDrag = useCallback(() => {
        setDrag(null);
        setDragOverTarget(null);
    }, []);
    const copy = useCallback((ids) => {
        setClipboard(ids);
        setClipboardKind('copy');
    }, []);
    const cut = useCallback((ids) => {
        setClipboard(ids);
        setClipboardKind('cut');
    }, []);
    const clearClipboard = useCallback(() => {
        setClipboard(null);
        setClipboardKind(null);
    }, []);
    const openPreview = useCallback((itemId) => setPreviewItemId(itemId), []);
    const closePreview = useCallback(() => setPreviewItemId(null), []);
    const value = useMemo(() => ({
        selected,
        select,
        selectMany,
        clear,
        isSelected,
        drag,
        startDrag,
        endDrag,
        dragOverTarget,
        setDragOverTarget,
        clipboard,
        copy,
        cut,
        clipboardKind,
        clearClipboard,
        previewItemId,
        openPreview,
        closePreview,
    }), [selected, select, selectMany, clear, isSelected, drag, startDrag, endDrag, dragOverTarget, clipboard, copy, cut, clipboardKind, clearClipboard, previewItemId, openPreview, closePreview]);
    return _jsx(DesktopContext.Provider, { value: value, children: children });
}
export function useDesktop() {
    const v = useContext(DesktopContext);
    if (!v)
        throw new Error('useDesktop must be used inside <DesktopProvider>');
    return v;
}
